/**
 * Paying a pledge on the web (GH-809): the two decisions checkout makes differently when the gift
 * keeps a promise the donor already made.
 *
 * ## Why the context's method list is not read as it stands
 *
 * The checkout context describes *this event's* giving, and after an event — ended, archived, or
 * suspended — it marks every paying rail unavailable. That is right for a new gift and wrong for
 * keeping a promise: the owner's rule (2026-10-01, GH-800 addendum) is that paying a pledge is
 * never refused for the event's state, only for the organization's, and the server's quote and
 * payment intent apply exactly that rule. iOS skips the event gate in the same case.
 *
 * So while a pledge is being paid, the card-class rails (card and the wallets) are offered wherever
 * the organization can accept donations. This changes what the screen *offers*, never what the
 * server *accepts*: the quote still refuses a pledge that is not the caller's, not open, or for
 * another event, and the payment intent still refuses an organization that cannot take money.
 *
 * **Bank is left exactly as the context says.** The context cannot tell a bank rail switched off
 * for the deployment from one switched off for the event, so a bank box re-enabled here could be a
 * button the server refuses. Card-class is the rail every deployment has.
 *
 * **Pledge is never offered** while paying one: promising to keep a promise is not a gift.
 */
import type { ContextResponse, PaymentMethodOption } from "@givehere/contracts";

/** The rails a promise may be paid with whatever became of its event. */
const CARD_CLASS = new Set<PaymentMethodOption["id"]>(["card", "apple_pay", "google_pay"]);

/**
 * The method list to show while paying a pledge.
 *
 * An organization that cannot accept donations gets the context's list unchanged (and the screen
 * refuses before it is drawn): the organization is the one gate a promise still answers to.
 */
export function methodsForPledgePayment(
  context: Pick<ContextResponse, "organization" | "paymentMethods">,
): readonly PaymentMethodOption[] {
  if (!context.organization.canAcceptDonations) return context.paymentMethods;
  return context.paymentMethods.map((option) => {
    if (option.id === "pledge") {
      return { ...option, available: false, unavailableReason: "You are paying a pledge." };
    }
    if (CARD_CLASS.has(option.id) && !option.available) {
      return { ...option, available: true, unavailableReason: null };
    }
    return option;
  });
}

/** The `?pledge=<id>` arrival from "Pay pledge": an identifier only, checked against the server. */
export function pledgeToPay(search: string): string | null {
  const value = new URLSearchParams(search).get("pledge")?.trim() ?? "";
  // Shape only. Ownership, openness and the event are the server's to decide.
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
    ? value
    : null;
}
